import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { InlineError, StatePanel } from '@/components/ui/StatePanel';

import type { AuditSummary } from '../types';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

interface Tile {
  key: keyof AuditSummary;
  label: string;
  detail: string;
  tone?: 'ok' | 'red';
}

const TILES: Tile[] = [
  { key: 'today', label: 'Eventos hoy', detail: 'empresa activa' },
  { key: 'month', label: 'Este mes', detail: 'empresa activa' },
  { key: 'signaturesMonth', label: 'Firmas del mes', detail: 'aprobaciones selladas', tone: 'ok' },
  { key: 'returnsMonth', label: 'Devoluciones del mes', detail: 'reprocesos por Calidad' },
];

/** KPIs de la bitácora de la empresa activa. Muestran datos reales del backend;
 *  mientras cargan, un guion en vez de un número inventado. Si la consulta falla sin datos
 *  se reemplazan por el aviso con reintento, igual que los del Panel del QMS (UX-ERR-01); si
 *  falla una recarga, se conservan los anteriores y se avisa. */
export function TraceKpis({
  data,
  isLoading,
  error,
}: {
  data: AuditSummary | undefined;
  isLoading: boolean;
  error?: { onRetry: () => void; retrying: boolean; cause: unknown } | null;
}): ReactElement {
  if (error && data === undefined) {
    return (
      <StatePanel
        icon={<AlertTriangle size={20} />}
        title="No se pudieron cargar los indicadores"
        tone="danger"
        role="alert"
        onRetry={error.onRetry}
        retrying={error.retrying}
      >
        {errorAdvice(error.cause)}
      </StatePanel>
    );
  }
  return (
    <>
      <div className="tiles rise d1">
        {TILES.map((t) => {
          const value = data?.[t.key];
          // Las devoluciones se marcan en rojo solo si hay alguna (atención real).
          const tone = t.key === 'returnsMonth' ? (value && value > 0 ? 'red' : undefined) : t.tone;
          return (
            <div key={t.key} className={cn('tile', tone)}>
              <div className="k">{t.label}</div>
              <div className="v" aria-busy={isLoading || undefined}>
                {isLoading || value === undefined ? '—' : value.toLocaleString('es-CR')}
              </div>
              <div className="d">{t.detail}</div>
            </div>
          );
        })}
      </div>
      {error && (
        <div className="card" style={{ marginTop: 8 }}>
          <InlineError
            icon={<RefreshCw size={16} />}
            onRetry={error.onRetry}
            retrying={error.retrying}
          >
            No se pudieron actualizar los indicadores. Se muestran los anteriores.
            {refreshAdvice(error.cause)}
          </InlineError>
        </div>
      )}
    </>
  );
}
